import { useState, useEffect } from 'react'
import {
  Loader2,
  Send,
  Inbox,
  Mail,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  FileText,
  Calendar,
  Users,
  Scale,
  Trophy,
  Archive,
  HelpCircle,
  StickyNote,
  DollarSign,
  Banknote,
  Gavel,
  Shield,
  Eye,
  EyeOff,
} from 'lucide-react'
import { legalAPI } from '@/utils/api'
import { Badge } from '@/components/ui/badge'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'

interface TimelineEvent {
  id: string
  event_type: 'action' | 'correspondence'
  title: string
  description: string
  icon: string
  date: string
  deadline: string | null
  visible_to_client: boolean
  metadata: Record<string, unknown> | null
  created_by_name: string | null
  party_type: string | null
  party_name: string | null
  direction: string | null
  channel: string | null
  status: string | null
}

interface CaseTimelineProps {
  caseId: number
  showVisibilityToggle?: boolean
}

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  'send': Send,
  'inbox': Inbox,
  'mail': Mail,
  'check-circle': CheckCircle,
  'x-circle': XCircle,
  'alert-circle': AlertCircle,
  'clock': Clock,
  'file-text': FileText,
  'calendar': Calendar,
  'users': Users,
  'scale': Scale,
  'trophy': Trophy,
  'archive': Archive,
  'help-circle': HelpCircle,
  'sticky-note': StickyNote,
  'dollar-sign': DollarSign,
  'banknote': Banknote,
  'gavel': Gavel,
  'shield': Shield,
  'check': CheckCircle,
  'file-question': HelpCircle,
  'file-check': FileText,
  'handshake': Users,
  'circle': AlertCircle,
}

function getIcon(iconName: string) {
  return ICON_MAP[iconName] || AlertCircle
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function formatDateTime(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) {
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    if (diffHours === 0) {
      const diffMinutes = Math.floor(diffMs / (1000 * 60))
      return `vor ${diffMinutes} Min.`
    }
    return `vor ${diffHours} Std.`
  }
  if (diffDays === 1) return 'gestern'
  if (diffDays < 7) return `vor ${diffDays} Tagen`
  if (diffDays < 30) return `vor ${Math.floor(diffDays / 7)} Wochen`
  return formatDate(dateString)
}

function getEventColor(event: TimelineEvent): string {
  // Correspondence events
  if (event.event_type === 'correspondence') {
    if (event.direction === 'outbound') return 'bg-sidebar-active'
    return 'bg-green-500'
  }

  // Action events - by type
  const type = event.metadata?.action_type as string || ''
  if (type.startsWith('rsv_')) {
    if (type === 'rsv_approved') return 'bg-green-500'
    if (type === 'rsv_denied') return 'bg-red-500'
    return 'bg-amber-500'
  }
  if (type.startsWith('mahnschreiben_')) return 'bg-orange-500'
  if (type.startsWith('klage_') || type.startsWith('court_') || type.startsWith('hearing_') || type === 'judgment') return 'bg-purple-500'
  if (type.startsWith('defendant_')) return 'bg-slate-500'
  if (type === 'case_won' || type === 'settlement_reached' || type === 'payment_received') return 'bg-green-500'
  if (type === 'case_lost') return 'bg-red-500'
  if (type.startsWith('client_')) return 'bg-cyan-500'

  return 'bg-slate-400'
}

export function CaseTimeline({ caseId, showVisibilityToggle = true }: CaseTimelineProps) {
  const [timeline, setTimeline] = useState<TimelineEvent[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchTimeline = async () => {
      setIsLoading(true)
      try {
        const response = await legalAPI.getTimeline(caseId)
        setTimeline(response.timeline || [])
      } catch (error) {
        console.error('Failed to fetch timeline:', error)
        toast.error('Fehler beim Laden des Verlaufs')
      } finally {
        setIsLoading(false)
      }
    }

    fetchTimeline()
  }, [caseId])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 text-slate-400 animate-spin" />
      </div>
    )
  }

  if (timeline.length === 0) {
    return (
      <div className="text-center py-12">
        <Clock className="h-12 w-12 text-slate-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-slate-700 mb-2">Noch keine Aktivitäten</h3>
        <p className="text-slate-500 text-sm">
          Erstellen Sie eine Aktion oder Korrespondenz, um den Fallverlauf zu starten.
        </p>
      </div>
    )
  }

  return (
    <div className="relative">
      {/* Timeline line */}
      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200" />

      {/* Timeline items */}
      <div className="space-y-6">
        {timeline.map((event, index) => {
          const IconComponent = getIcon(event.icon)
          const eventColor = getEventColor(event)

          return (
            <div key={event.id} className="relative pl-10">
              {/* Icon */}
              <div
                className={cn(
                  'absolute left-0 w-8 h-8 rounded-full flex items-center justify-center',
                  eventColor
                )}
              >
                <IconComponent className="h-4 w-4 text-white" />
              </div>

              {/* Content */}
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-medium text-slate-900 truncate">{event.title}</h4>
                      {event.party_type && (
                        <Badge variant="outline" className="text-xs">
                          {event.party_type === 'rsv' && 'RSV'}
                          {event.party_type === 'court' && 'Gericht'}
                          {event.party_type === 'defendant' && 'Gegner'}
                          {event.party_type === 'client' && 'Mandant'}
                          {event.party_type === 'other' && 'Sonstige'}
                        </Badge>
                      )}
                      {event.direction && (
                        <Badge
                          variant="outline"
                          className={cn(
                            'text-xs',
                            event.direction === 'outbound' ? 'text-highlight-text' : 'text-green-600'
                          )}
                        >
                          {event.direction === 'outbound' ? 'Ausgehend' : 'Eingehend'}
                        </Badge>
                      )}
                    </div>

                    {event.description && (
                      <p className="text-sm text-slate-500 mt-1">{event.description}</p>
                    )}

                    {event.party_name && (
                      <p className="text-sm text-slate-600 mt-1">
                        {event.direction === 'outbound' ? 'An: ' : 'Von: '}
                        {event.party_name}
                      </p>
                    )}

                    {event.deadline && (
                      <p className="text-sm text-red-600 mt-2 flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        Frist: {formatDate(event.deadline)}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col items-end gap-1">
                    <span className="text-xs text-slate-500" title={formatDateTime(event.date)}>
                      {formatRelativeTime(event.date)}
                    </span>
                    {showVisibilityToggle && (
                      <span
                        className={cn(
                          'flex items-center gap-1 text-xs',
                          event.visible_to_client ? 'text-green-600' : 'text-slate-400'
                        )}
                        title={event.visible_to_client ? 'Für Mandant sichtbar' : 'Nicht für Mandant sichtbar'}
                      >
                        {event.visible_to_client ? (
                          <Eye className="h-3 w-3" />
                        ) : (
                          <EyeOff className="h-3 w-3" />
                        )}
                      </span>
                    )}
                  </div>
                </div>

                {/* Created by */}
                {event.created_by_name && (
                  <div className="mt-2 pt-2 border-t border-slate-100 text-xs text-slate-400">
                    {event.created_by_name}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
